import {
  defaultTo,
  ifElse,
  pipe,
  identity,
  always,
  is,
  isNil,
  unless,
} from 'ramda';
import {
  format,
  isSameDay,
  isSameMonth,
  isSameYear,
  isThisYear,
  getMinutes,
  roundToNearestMinutes,
  addMinutes,
  isFuture,
  parse,
  isValid,
} from 'date-fns';
import {parseISO} from 'date-fns/fp';
import {utcToZonedTime, zonedTimeToUtc} from 'date-fns-tz';
import z from 'zod';
import {isNotNil} from './TypescriptUtils';

type FormatOptions = {
  dayOfWeekFmt?: string;
  dayFmt?: string;
  monthFmt?: string;
  yearFmt?: string;
  /** Prevent future dates from showing by limiting the displayed end date to the current date */
  limitEndDate?: boolean;
};

export const formatDateRange = (
  startDate: Date | string,
  endDate: Date | string,
  options?: FormatOptions,
) => {
  let isInvalidDate = false;
  const dates = {start: new Date(startDate), end: new Date(endDate)};

  if (options?.limitEndDate && isFuture(dates.end)) {
    dates.end = new Date();
  }

  const checkDateError = (name: keyof typeof dates) => {
    if (Number.isNaN(dates[name].getDate())) isInvalidDate = true;
  };
  Object.keys(dates).forEach(name =>
    checkDateError(name as keyof typeof dates),
  );
  if (isInvalidDate) return 'Invalid date range';

  const startSameYear = isThisYear(dates.start);
  const endSameYear = isThisYear(dates.end);
  const sameYear = isSameYear(dates.start, dates.end);
  const sameMonth = isSameMonth(dates.start, dates.end);
  const sameDay = isSameDay(dates.start, dates.end);

  const {
    dayOfWeekFmt = 'EEE, ',
    dayFmt = 'do',
    monthFmt = 'MMM',
    yearFmt = 'yyyy',
  } = options || {};

  const startFormatStr = (() => {
    switch (true) {
      case !sameYear:
        return `${dayOfWeekFmt}${monthFmt} ${dayFmt}, ${yearFmt}`;
      case sameYear && sameDay && !startSameYear:
        return `${dayOfWeekFmt}${monthFmt} ${dayFmt}, ${yearFmt}`;
      default:
        return `${dayOfWeekFmt}${monthFmt} ${dayFmt}`;
    }
  })();

  const endFormatStr = (() => {
    switch (true) {
      case endSameYear && sameMonth:
        return `${dayFmt}`;
      case endSameYear && !sameMonth:
        return `${monthFmt} ${dayFmt}`;
      case !endSameYear && sameMonth:
        return `${dayFmt}, ${yearFmt}`;
      case !endSameYear && !sameMonth:
        return `${monthFmt} ${dayFmt}, ${yearFmt}`;
      default:
        return '';
    }
  })();

  const startText = format(dates.start, startFormatStr);
  const endFormatted = format(dates.end, endFormatStr);
  const endText = sameDay ? '' : ` - ${endFormatted}`;

  return startText + endText;
};

/**
 * @deprecated use format() directly with a CommonDateFormat
 */
export const formatSingleDate = (date: Date) =>
  Number.isNaN(date.getDate())
    ? 'Invalid date'
    : format(date, `EEE, MMM do, yyyy`);

/**
 * @deprecated It turns out that this just doesn't work as expected. Better to explicitly use `format` from date-fns-tz and specify the expected timezone
 * @see https://date-fns.org/v2.16.1/docs/Time-Zones
 */
export const userZonedTimeToUtc = (
  time: string | number | Date,
  userTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
) => zonedTimeToUtc(time, userTimeZone);

/**
 * NOTE: This should _only_ be used if the provided date string is not provided in ISO format.
 * If the date is provided in ISO format, then parseISO is strictly superior to this helper.
 */
export const utcToUserZonedTime = (
  time: string | number | Date,
  userTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
) => utcToZonedTime(time, userTimeZone, {timeZone: 'UTC'});

export const CommonDateFormats = {
  'Mon, Jan 20': 'eee, LLL d',
  'Mon, Jan 20, 2020': 'eee, LLL d, yyyy',
  'Jan 20, 2020': 'LLL d, yyyy',
  'January 20, 2020': 'MMMM d, yyyy',
  '20 Jan, 2020': 'd LLL, yyyy',
  '1/5/2020': 'M/d/yyyy',
  '01/20/2020': 'MM/dd/yyyy',
  '20/01/2020': 'dd/MM/yyyy',
  '2020-01-20': 'yyyy-MM-dd',
  '2020-01-20 23:59:59': 'yyyy-MM-dd HH:mm:ss',
  '2020-01-20 11:59:59PM': 'yyyy-MM-dd hh:mm:ssaa',
  '2020-01-20T01:59:00-0800': `yyyy-MM-dd'T'HH:mm:ssxx`,
  'Jan 20, 2020 1:59pm': 'LLL d, yyyy h:mmaaa',
  'Saturday, Sep 27, 2023': 'EEEE, LLL d, yyyy',
  'Monday, January 20, 2020; 11:59pm': 'EEEE, LLL d, yyyy; h:mma',
};

const isValidDate = (date: Date) => !Number.isNaN(date.getTime());
export const parseIsoOrNull: (
  date: string | Date | null | undefined,
) => Date | null = ifElse(
  isNotNil,
  pipe(
    // @ts-expect-error
    ifElse(is(Date), identity, parseISO),
    ifElse(isValidDate, identity, always(null)),
  ),
  defaultTo(null),
);

/** Works like roundToNearestMinutes in date-fns, but always rounds up (including values that line up perfectly with the step)  */
export const getNextDateInStepMinutes = (
  date: Date,
  /** Step between dates in minutes */
  step: number,
) => {
  const currentMinute = getMinutes(date);
  return roundToNearestMinutes(
    addMinutes(
      currentMinute % step === 0 ? addMinutes(date, 1) : date,
      step / 2,
    ),
    {nearestTo: step},
  );
};

/**
 * A dependency-free function to check if a value is Moment
 */
export const isMoment = (value: unknown) =>
  typeof value === 'object' &&
  value != null &&
  '_isAMomentObject' in value &&
  // @ts-ignore
  value._isAMomentObject != null;

/** Parse a date from an API response */
export const parseNullableDate = unless(isNil, parseISO);

/**
 * Parse a date from a non-standard format and convert to UTC.
 * Useful for handling API responses that send dates in formats like "YYYY-MM-DD HH:mm:ss"
 * that are meant to be interpreted as UTC but don't follow ISO standards.
 *
 * @example
 * ```typescript
 * // Parse "2024-01-15 14:30:00" as UTC
 * const date = parseNonStandardUtcDate('2024-01-15 14:30:00');
 *
 * // Parse with custom format
 * const date = parseNonStandardUtcDate('15/01/2024 14:30', 'dd/MM/yyyy HH:mm');
 * ```
 */
export const parseNonStandardUtcDate = (
  dateString: string,
  format: string,
): Date | null => {
  try {
    const parsedDate = parse(dateString, format, new Date());

    if (!isValid(parsedDate)) {
      return null;
    }

    // Treat the parsed date as UTC and project it into the local timezone
    return new Date(
      parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000,
    );
  } catch (error) {
    return null;
  }
};

/**
 * Create a Zod schema for parsing non-standard date formats.
 * Useful for API responses that send dates in non-ISO formats.
 *
 * @example
 * ```typescript
 * const schema = z.object({
 *   createdAt: createNonStandardUtcDateSchema(),
 *   updatedAt: createNonStandardUtcDateSchema('yyyy-MM-dd HH:mm:ss', 'Invalid updated date'),
 * });
 * ```
 */
export const createNonStandardUtcDateSchema = (
  format: string,
  errorMessage?: string,
) => {
  return z.string().transform((val, ctx) => {
    const parsedDate = parseNonStandardUtcDate(val, format);

    if (parsedDate === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: errorMessage ?? `Invalid date format. Expected "${format}"`,
      });
      return z.NEVER;
    }

    return parsedDate;
  });
};
